import React, { useEffect, useMemo, useState } from 'react';
import { Combobox, Option } from '@fluentui/react-components';
import { filterProtocols, protocolLabel } from '../../../functions/ProviderConsumptionImport.Functions';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';

/** Opciones dibujadas (se filtra escribiendo "id" o parte del nombre). */
export const MAX_PROTOCOL_OPTIONS = 100;

export interface ProtocolPickerProps {
  protocols: Protocol[];
  value: number | undefined;
  onChange: (protocolId: number | undefined) => void;
  placeholder: string;
  emptyText: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Selector de protocolo "id - nombre" (Combobox v9 con búsqueda). Borrar el texto quita la selección.
 * Va dentro de un `GuideField` (etiqueta, ayuda y error los pone el campo).
 */
export const ProtocolPicker = ({ protocols, value, onChange, placeholder, emptyText, disabled, 'data-testid': testId }: ProtocolPickerProps) => {
  const selected = protocols.find((protocol) => protocol.id === value);
  const [query, setQuery] = useState(selected ? protocolLabel(selected) : value ? String(value) : '');

  // Sincroniza el texto cuando el valor cambia desde afuera (limpiar filtros, lista que llega después).
  useEffect(() => {
    if (value === undefined) setQuery('');
    else if (selected) setQuery(protocolLabel(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selected?.id]);

  const options = useMemo(() => filterProtocols(protocols, query === protocolLabel(selected) ? '' : query).slice(0, MAX_PROTOCOL_OPTIONS), [protocols, query, selected]);

  return (
    <Combobox
      freeform
      clearable
      placeholder={placeholder}
      value={query}
      selectedOptions={value ? [String(value)] : []}
      disabled={disabled}
      onChange={(event) => {
        setQuery(event.target.value);
        if (value !== undefined) onChange(undefined);
      }}
      onOptionSelect={(_event, data) => {
        const protocol = protocols.find((item) => String(item.id) === data.optionValue);
        setQuery(protocol ? protocolLabel(protocol) : '');
        onChange(protocol?.id);
      }}
      data-testid={testId}
    >
      {options.length === 0 ? (
        <Option value="" text="" disabled>
          {emptyText}
        </Option>
      ) : (
        options.map((protocol) => (
          <Option key={protocol.id} value={String(protocol.id)} text={protocolLabel(protocol)}>
            {protocolLabel(protocol)}
          </Option>
        ))
      )}
    </Combobox>
  );
};

export default ProtocolPicker;
